import PropTypes from 'prop-types';
import React from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import TransactionalSMS from 'yoda-checkout-components/lib/components/TransactionalSMS/TransactionalSMS';
import { URL_CHECKOUT_PAYMENT } from 'yoda-checkout-components/lib/common/Constants';
import {
    ADD_GIFT_CARD,
    ADD_ANOTHER_GIFT_CARD,
} from 'yoda-checkout-components/lib/common/LabelConstants';
import User from 'yoda-site-components/lib/helpers/User/User';
import * as PlaceOrderActions from 'yoda-checkout-components/lib/actions/PlaceOrderActions';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { isAgentLogged } from 'yoda-core-components/lib/helpers/Utils/Utils';
import PaymentItem from './PaymentItem/PaymentItem';
import * as GetCartDetailsAction from '../../../../../actions/GetCartDetailsAction';
import * as newPaymentAction from '../../../../../actions/NewPaymentAction';
import * as checkoutPageActions from '../../../../../actions/CheckoutPageActions';
import checkoutStaticText from '../../../../../helpers/LabelConstantsCheckout';
import ContactInformation from '../../../../../components/ContactInformation/ContactInformation';
import AddGiftCardPaymentCTA from '../../../../../components/AddGiftCardPaymentCTA/AddGiftCardPaymentCTA';
import BillingAddressDetails from '../../../../../components/BillingAddressDetails/BillingAddressDetails';
import PromotionalEmailOptIn from '../../../../../components/PromotionalEmailOptIn/PromotionalEmailOptIn';
import SpecialFinancingOfferInformation from '../../../../../components/SpecialFinancingOfferInformation/SpecialFinancingOfferInformation';
import { selectBnplOnOrder } from '../../../../../selectors/NewPaymentMethodSelector';

export function PaymentSection(props) {
    const {
        orderContactInfo,
        orderPaymentInfo,
        orderBillingInfo,
        orderInfo,
        context,
        isPriceAccurate,
        giftCardInfo,
        appliedGiftCardList,
        messagesTexts,
        contactMethods,
    } = props;
    const phoneNumber = orderContactInfo.phoneNumber ? orderContactInfo.phoneNumber : undefined;
    const isBnplOnOrder = useSelector(selectBnplOnOrder);
    const showAddGiftCardLink = get(orderInfo, 'showAddGiftCardLink') && !isBnplOnOrder;
    const { isTransactionalSMSEnabled = true, registerEmailOptInLabel = true } =
        context?.featureFlags || {};
    const buttonCTALabel =
        appliedGiftCardList.length > 0
            ? ADD_ANOTHER_GIFT_CARD
            : get(giftCardInfo, 'buttonCTALabel', ADD_GIFT_CARD);
    const isNative = get(context, 'isNative', false);
    const onClickAddGiftCard = () => {
        props.actions.initNewCheckoutSectionsClientSideActions(URL_CHECKOUT_PAYMENT, true);
        props.actions.showHideGiftCardForm(true);
        props.actions.resetPromoFinancingData();
        isNative && props.actions.getAppGiftCards();
    };

    const renderAddGiftCardLink = () => (
        <div className={dt(['text-medium'])} data-automation-id="at-add-a-gift-card">
            <AddGiftCardPaymentCTA
                buttonCTALabel={buttonCTALabel}
                buttonCTAAction={onClickAddGiftCard}
                context={context}
                renderCTA
            />
        </div>
    );
    let isUserLoggedIn = User.isUserLoggedIn();
    if (__SERVER__) {
        isUserLoggedIn = get(context, 'headers.cookies.DP_USER_STATE') === '1';
    }

    const isOptedEmail = contactMethods?.find((el) => toLower(el?.type) === 'email')?.optin;
    const optedEmail = contactMethods?.find((el) => toLower(el?.type) === 'email')?.value || '';
    const implCustomLogic =
        registerEmailOptInLabel &&
        isOptedEmail !== undefined &&
        isOptedEmail === false &&
        isUserLoggedIn;

    let adjustStylingForTransactionalSMS = [];
    if (implCustomLogic) {
        adjustStylingForTransactionalSMS = ['pt-5', 'pb-1', 'bg-light-white', 'rounded-lg'];
    } else {
        adjustStylingForTransactionalSMS = ['mb-6'];
    }

    const voucherNumber = TokenProvider.get('Voucher_Number') || null;
    return (
        <div
            className={dt(['bg-white', 'lg:p-6', 'smMd:pt-6', 'lg:m-2', 'smMd:px-0'])}
            data-automation-id="at-review-payment-section-container"
        >
            <div
                className={dt(['font-open-sans-semibold', 'text-medium', 'mb-2'])}
                data-automation-id="at-review-billing-info-payment-method-title"
            >
                {checkoutStaticText.PAYMENT_METHODS}
            </div>
            <PaymentItem
                isPriceAccurate={isPriceAccurate}
                paymentList={orderPaymentInfo}
                billingInfo={orderBillingInfo}
                context={context}
            />
            <div>
                <div
                    className={`${'sm12 md12'} ${dt(['mt-4', '!p-0'])}`}
                    data-automation-id="at-review-billing-info-container"
                >
                    <BillingAddressDetails billingInfo={orderBillingInfo} source="review" />
                </div>
            </div>
            <div>
                <div
                    className={`${'sm12 md12'} ${dt(['mt-6', '!p-0'])}`}
                    data-automation-id="at-review-contact-info-container"
                >
                    <ContactInformation
                        userEmailId={orderContactInfo.emailId}
                        userPhoneNumber={orderBillingInfo.phone}
                        source="review"
                    />
                </div>
            </div>
            {isAgentLogged(context) && voucherNumber ? (
                <div>
                    <div
                        className={`${'sm12 md12'} ${dt(['mt-6', '!p-0'])}`}
                        data-automation-id="at-voucher-number-container"
                    >
                        <div
                            data-automation-id="at-voucher-number-title"
                            className={dt(['font-open-sans-semibold', 'text-medium'])}
                        >
                            Voucher Number
                        </div>
                    </div>
                    <div className={`${dt(['text-small'])}`}>
                        <div
                            is
                            x-ms-format-detection="none"
                            data-automation-id="at-voucher-number"
                            data-qm-block="true"
                            data-tlprivate
                            className={dt(['!no-underline'])}
                        >
                            {voucherNumber || ''}
                        </div>
                    </div>
                </div>
            ) : (
                ''
            )}

            {!isAgentLogged(context) && (
                <div
                    className={dt(['mt-6', 'mb-6', ...adjustStylingForTransactionalSMS])}
                    data-automation-id="at-review-trans-sms-container"
                >
                    {implCustomLogic && (
                        <PromotionalEmailOptIn
                            isOptedEmail={!(isOptedEmail !== undefined && isOptedEmail === true)}
                            messagesTexts={messagesTexts}
                            email={optedEmail}
                            actions={props.actions}
                        />
                    )}
                    {isTransactionalSMSEnabled && (
                        <TransactionalSMS
                            SMSNumber={phoneNumber}
                            callbackAction={GetCartDetailsAction.getCartDetails()}
                            componentName="REVIEW_PAYMENT_SECTION"
                            messagesTexts={messagesTexts}
                            renderAddGCLink={showAddGiftCardLink}
                            registerEmailOptInLabel={implCustomLogic}
                        />
                    )}
                </div>
            )}
            {showAddGiftCardLink && renderAddGiftCardLink()}
            <div>
                {!isEmpty(orderInfo.financing) ? (
                    <SpecialFinancingOfferInformation financing={orderInfo.financing} />
                ) : (
                    ''
                )}
            </div>
        </div>
    );
}

PaymentSection.propTypes = {
    orderBillingInfo: PropTypes.objectOf(PropTypes.object),
    orderContactInfo: PropTypes.objectOf(PropTypes.object),
    orderPaymentInfo: PropTypes.arrayOf(PropTypes.object),
    orderInfo: PropTypes.objectOf(PropTypes.object),
    context: PropTypes.objectOf(PropTypes.object).isRequired,
    actions: PropTypes.objectOf(PropTypes.func).isRequired,
    isPriceAccurate: PropTypes.bool,
    appliedGiftCardList: PropTypes.arrayOf(PropTypes.object),
    messagesTexts: PropTypes.objectOf(PropTypes.object),
    contactMethods: PropTypes.arrayOf(PropTypes.object),
    giftCardInfo: PropTypes.objectOf(PropTypes.object),
};

PaymentSection.defaultProps = {
    orderBillingInfo: {},
    orderContactInfo: {},
    orderPaymentInfo: [],
    orderInfo: {},
    isPriceAccurate: false,
    appliedGiftCardList: [],
    messagesTexts: {},
    contactMethods: [],
    giftCardInfo: {},
};

const mapStateToProps = ({
    orderBillingInfo,
    orderContactInfo,
    orderPaymentInfo,
    orderInfo,
    context,
    orderDetails,
    newPayment,
}) => ({
    orderBillingInfo,
    orderContactInfo,
    orderPaymentInfo,
    orderInfo,
    context,
    newPayment,
    isPriceAccurate: get(orderDetails, 'data.isPriceAccurate', false),
    giftCardInfo: get(newPayment, 'giftCardInfo', {}),
    appliedGiftCardList: get(orderDetails, 'data.billing.unnamed.giftCards', []),
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            ...checkoutPageActions,
            ...newPaymentAction,
            ...PlaceOrderActions,
        },
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentSection);
